import { Text } from '@/components/ui';
import { StatusBadge } from '@/features/contracts/components/StatusBadge';
import { format, differenceInDays } from 'date-fns';
import { parseLocalDate } from '@/lib/utils/parse-local-date';
import { StatusType } from '@/lib/api/dashboard';

interface ExpiringContractCardProps {
  contractName: string;
  status: StatusType;
  expirationDate?: Date;
  hoursConsumed?: number;
  totalHours?: number;
}

export function ExpiringContractCard({
  contractName,
  status,
  expirationDate,
  hoursConsumed,
  totalHours,
}: ExpiringContractCardProps) {
  const calculateDaysRemaining = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiration = new Date(date);
    expiration.setHours(0, 0, 0, 0);
    return differenceInDays(expiration, today);
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'EXPIRED':
        if (!expirationDate) return null;
        return `Venceu dia ${format(parseLocalDate(expirationDate.toISOString()), 'dd/MM/yyyy')}`;
      case 'EXPIRING':
        if (!expirationDate) return null;
        const daysRemaining = calculateDaysRemaining(expirationDate);
        return `Vence em ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'}`;
      case 'LOW_HOURS':
        if (!totalHours || !hoursConsumed) return null;
        const remainingHours = totalHours - hoursConsumed;
        return `Restam apenas ${remainingHours}h`;
      case 'HOURS_EXCEED':
        if (!totalHours || !hoursConsumed) return null;
        const exceededHours = hoursConsumed - totalHours;
        return `Ultrapassou em ${exceededHours}h`;
      default:
        return null;
    }
  };

  return (
    <div className="bg-base-white border-transparent-dark-12 p-md rounded-2xl border shadow-xs">
      <div className="gap-4xs flex flex-col items-start">
        <Text size="sm" strong className="line-clamp-2 flex-1">
          {contractName}
        </Text>
        <StatusBadge status={status} />
        {getStatusMessage() && (
          <Text size="sm" className="text-neutral-muted">
            {getStatusMessage()}
          </Text>
        )}
      </div>
    </div>
  );
}
